import React, { useRef, useMemo, useLayoutEffect } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { useStore } from "../store";

// River / harbour routes (closed CatmullRom loops).
const ROUTES = {
  hudson: { pts: [[-740, -300], [-470, -300], [-470, 900], [-740, 900]], y: 1.0 },
  east: { pts: [[560, -760], [1080, -760], [1080, 760], [560, 760]], y: 1.0 },
  // Battery Park <-> Liberty Island shuttle (Statue of Liberty ferry)
  statue: { pts: [[-560, 480], [-680, 600], [-800, 760], [-720, 820], [-580, 660], [-520, 520]], y: 1.0 },
};

function loop(pts) {
  return new THREE.CatmullRomCurve3(pts.map(([x, z]) => new THREE.Vector3(x, 0, z)), true, "catmullrom", 0.05);
}

// Pointed boat hull (top-view outline extruded downward), bow toward +Z (forward).
function makeHull(len, wid, ht) {
  const s = new THREE.Shape();
  s.moveTo(-wid / 2, -len / 2);
  s.lineTo(wid / 2, -len / 2);
  s.lineTo(wid / 2, len * 0.2);
  s.quadraticCurveTo(wid / 2, len / 2, 0, len / 2);
  s.quadraticCurveTo(-wid / 2, len / 2, -wid / 2, len * 0.2);
  s.lineTo(-wid / 2, -len / 2);
  const g = new THREE.ExtrudeGeometry(s, { depth: ht, bevelEnabled: false });
  g.rotateX(Math.PI / 2); // length -> +Z (forward), height -> downward
  return g;
}

const KINDS = {
  ferry: { len: 34, wid: 10, ht: 5, hull: { present: "#e0641c", "2001": "#c8551a" }, cabin: "#f2efe8", deckWin: "#2a3138", funnel: "#e0641c" },
  tug: { len: 18, wid: 7, ht: 5, hull: { present: "#7a2b22", "2001": "#6e2a20" }, cabin: "#e9e5dc", deckWin: "#243038", funnel: "#1c1c1c" },
  taxi: { len: 16, wid: 6, ht: 3.6, hull: { present: "#f4c20d", "2001": "#f4c20d" }, cabin: "#1f242a", deckWin: "#0e1114", funnel: null },
  sail: { len: 13, wid: 4, ht: 2.6, hull: { present: "#f2f0ea", "2001": "#efe9dc" }, cabin: null, deckWin: null, funnel: null },
};

// Static vessel geometry for a given kind + era.
function Vessel({ kind, era }) {
  const cfg = KINDS[kind];
  const hullGeo = useMemo(() => makeHull(cfg.len, cfg.wid, cfg.ht), [kind]);
  const hullColor = cfg.hull[era] || cfg.hull.present;
  return (
    <group>
      <mesh geometry={hullGeo} castShadow>
        <meshStandardMaterial color={hullColor} roughness={0.6} metalness={0.15} />
      </mesh>
      {/* boot-stripe / gunwale */}
      <mesh position={[0, 0.1, 0]}>
        <boxGeometry args={[cfg.wid + 0.2, 0.5, cfg.len * 0.9]} />
        <meshStandardMaterial color={era === "2001" ? "#2c2c2c" : "#20242a"} roughness={0.7} />
      </mesh>
      {cfg.cabin && (
        <>
          <mesh position={[0, cfg.ht * 0.55, -cfg.len * 0.05]} castShadow>
            <boxGeometry args={[cfg.wid * 0.78, cfg.ht * 0.9, cfg.len * (kind === "ferry" ? 0.55 : 0.45)]} />
            <meshStandardMaterial color={cfg.cabin} roughness={0.55} metalness={0.1} />
          </mesh>
          {/* window band */}
          <mesh position={[0, cfg.ht * 0.7, -cfg.len * 0.05]}>
            <boxGeometry args={[cfg.wid * 0.8, cfg.ht * 0.28, cfg.len * (kind === "ferry" ? 0.56 : 0.46)]} />
            <meshStandardMaterial color={cfg.deckWin} metalness={0.4} roughness={0.25} emissive="#0a1418" emissiveIntensity={0.2} />
          </mesh>
          {/* upper deck for the ferry */}
          {kind === "ferry" && (
            <mesh position={[0, cfg.ht * 1.15, -cfg.len * 0.05]} castShadow>
              <boxGeometry args={[cfg.wid * 0.6, cfg.ht * 0.5, cfg.len * 0.4]} />
              <meshStandardMaterial color={cfg.cabin} roughness={0.55} />
            </mesh>
          )}
        </>
      )}
      {cfg.funnel && (
        <mesh position={[0, cfg.ht * 1.2, -cfg.len * 0.22]} castShadow>
          <cylinderGeometry args={[cfg.wid * 0.12, cfg.wid * 0.14, cfg.ht * 0.9, 10]} />
          <meshStandardMaterial color={cfg.funnel} roughness={0.5} metalness={0.2} />
        </mesh>
      )}
      {kind === "sail" && (
        <group position={[0, 0.4, cfg.len * 0.05]}>
          <mesh position={[0, cfg.ht * 1.6, 0]}>
            <cylinderGeometry args={[0.14, 0.18, cfg.ht * 3.4, 6]} />
            <meshStandardMaterial color="#c9bda6" roughness={0.7} />
          </mesh>
          <mesh position={[0, cfg.ht * 1.7, 1.4]} rotation={[0, Math.PI / 2, 0]}>
            <planeGeometry args={[5.2, cfg.ht * 3]} />
            <meshStandardMaterial color="#fbfbf7" roughness={0.8} side={THREE.DoubleSide} />
          </mesh>
        </group>
      )}
    </group>
  );
}

// A vessel that cruises a route; era-specific vessels hide when the timeline is on the other era.
function MovingBoat({ curve, y, u0, speed, kind, only }) {
  const g = useRef();
  const u = useRef(u0);
  const p = useMemo(() => new THREE.Vector3(), []);
  const t = useMemo(() => new THREE.Vector3(), []);
  const cfg = KINDS[kind];
  const era = only === "2001" ? "2001" : "present";

  const setAt = (uu) => {
    curve.getPointAt(uu, p);
    curve.getTangentAt(uu, t);
    if (g.current) {
      g.current.position.set(p.x, y, p.z);
      g.current.rotation.y = Math.atan2(t.x, t.z);
    }
  };
  useLayoutEffect(() => setAt(u0)); // place before first frame (never at origin)

  useFrame((_, delta) => {
    if (!g.current) return;
    const present = useStore.getState().temporal > 0.5;
    const vis = only === "present" ? present : only === "2001" ? !present : true;
    g.current.visible = vis;
    if (!vis) return;
    u.current = (u.current + speed * delta) % 1;
    setAt(u.current);
  });

  return (
    <group ref={g}>
      <Vessel kind={kind} era={era} />
      {/* foam wake */}
      <mesh position={[0, -0.35, -cfg.len * 0.7]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[cfg.wid * 1.3, cfg.len * 1.6]} />
        <meshStandardMaterial color="#eaf4f7" transparent opacity={0.3} depthWrite={false} />
      </mesh>
    </group>
  );
}

function Boats() {
  const hudson = useMemo(() => loop(ROUTES.hudson.pts), []);
  const east = useMemo(() => loop(ROUTES.east.pts), []);
  const statue = useMemo(() => loop(ROUTES.statue.pts), []);
  return (
    <group>
      {/* Hudson traffic */}
      <MovingBoat curve={hudson} y={1} u0={0.05} speed={0.006} kind="ferry" />
      <MovingBoat curve={hudson} y={1} u0={0.34} speed={0.005} kind="tug" />
      <MovingBoat curve={hudson} y={1} u0={0.6} speed={0.004} kind="sail" />
      <MovingBoat curve={hudson} y={1} u0={0.8} speed={0.0035} kind="sail" />
      <MovingBoat curve={hudson} y={1} u0={0.2} speed={0.007} kind="taxi" only="present" />
      <MovingBoat curve={hudson} y={1} u0={0.72} speed={0.006} kind="taxi" only="present" />
      {/* East River traffic */}
      <MovingBoat curve={east} y={1} u0={0.15} speed={0.005} kind="tug" />
      <MovingBoat curve={east} y={1} u0={0.55} speed={0.0045} kind="ferry" />
      <MovingBoat curve={east} y={1} u0={0.85} speed={0.005} kind="sail" />
      {/* Statue of Liberty shuttle */}
      <MovingBoat curve={statue} y={1} u0={0.1} speed={0.01} kind="ferry" />
      <MovingBoat curve={statue} y={1} u0={0.55} speed={0.011} kind="ferry" />
    </group>
  );
}

function Helicopter({ radius, altitude, speed, dir = 1 }) {
  const g = useRef();
  const rotor = useRef();
  useFrame((state, delta) => {
    const t = state.clock.elapsedTime * speed * dir;
    if (g.current) {
      g.current.position.set(-40 + Math.cos(t) * radius, altitude, -80 + Math.sin(t) * radius);
      g.current.rotation.y = -t + Math.PI / 2;
    }
    if (rotor.current) rotor.current.rotation.y += delta * 30;
  });
  return (
    <group ref={g}>
      <mesh castShadow>
        <capsuleGeometry args={[2.2, 5, 6, 10]} />
        <meshStandardMaterial color="#22262c" metalness={0.6} roughness={0.4} />
      </mesh>
      <mesh position={[0, 0, -6]}>
        <boxGeometry args={[0.6, 0.6, 8]} />
        <meshStandardMaterial color="#22262c" metalness={0.6} roughness={0.4} />
      </mesh>
      <mesh ref={rotor} position={[0, 2.4, 0]}>
        <boxGeometry args={[16, 0.15, 0.7]} />
        <meshStandardMaterial color="#111" />
      </mesh>
      <mesh position={[0.2, 3.4, -6]} rotation={[0, 0, Math.PI / 2]}>
        <boxGeometry args={[4, 0.12, 0.4]} />
        <meshStandardMaterial color="#111" />
      </mesh>
    </group>
  );
}

export default function AmbientLife() {
  return (
    <group>
      <Boats />
      <Helicopter radius={360} altitude={300} speed={0.12} dir={1} />
      <Helicopter radius={520} altitude={430} speed={0.08} dir={-1} />
    </group>
  );
}
